/**
 * Template Validation Utilities
 * Validation functions for template data
 */

/**
 * Validate template name
 */
export const validateTemplateName = (name) => {
  if (!name.trim()) {
    return "Template name is required";
  }
  if (name.toLowerCase() === "test") {
    return "Name 'test' is not allowed";
  }
  if (!/^[a-z0-9_]+$/.test(name)) {
    return "Only lowercase letters, numbers, and underscores allowed";
  }
  return null;
};

/**
 * Validate template body
 */
export const validateBody = (body) => {
  if (!body.trim()) {
    return "Message body is required";
  }
  if (body.length > 1024) {
    return "Message body cannot exceed 1024 characters";
  }
  return null;
};

/**
 * Validate header based on format
 */
export const validateHeader = (headerFormat, headerText, headerFile, headerMediaUrl) => {
  if (headerFormat === "TEXT" && !headerText.trim()) {
    return "Header text is required when format is TEXT";
  }
  
  if (
    ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
    !headerFile &&
    !headerMediaUrl
  ) {
    return `Please upload a ${headerFormat.toLowerCase()} or use existing media URL`;
  }
  
  return null;
};

/**
 * Validate variables
 */
export const validateVariables = (variables) => {
  const headerVariables = variables.filter((v) => v.target === "header");
  const bodyVariables = variables.filter((v) => v.target === "body");

  if (headerVariables.length > 1) {
    return { headerText: "Only 1 variable allowed in header" };
  }

  if (bodyVariables.length > 10) {
    return { body: "Maximum 10 variables allowed in body" };
  }

  return null;
};

/**
 * Validate URL buttons
 */
export const validateUrlButtons = (buttons) => {
  const errors = {};
  
  buttons.forEach((btn) => {
    if (btn.type === "URL" && btn.url) {
      const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
      if (urlVariables.length > 1) {
        errors[`url_${btn.id}`] = "Only one variable allowed in URL";
      }
    }
  });

  return Object.keys(errors).length > 0 ? errors : null;
};

/**
 * Validate WABA account
 */
export const validateWabaAccount = (wabaAccount) => {
  if (!wabaAccount || !wabaAccount.wabaId || !wabaAccount.phoneId) {
    return "Please select a WhatsApp Business Account from the header";
  }
  return null;
};

/**
 * Validate entire template
 */
export const validateTemplate = (templateData, wabaAccount) => {
  const errors = {};

  // Validate name
  const nameError = validateTemplateName(templateData.name);
  if (nameError) errors.name = nameError;

  // Validate body
  const body = templateData.components.find(c => c.type === "BODY")?.text || "";
  const bodyError = validateBody(body);
  if (bodyError) errors.body = bodyError;

  // Validate WABA
  const wabaError = validateWabaAccount(wabaAccount);
  if (wabaError) errors.wabaId = wabaError;

  // Validate header
  const headerComponent = templateData.components.find(c => c.type === "HEADER");
  const headerFormat = headerComponent?.format || "NONE";
  const headerText = headerComponent?.text || "";
  const headerError = validateHeader(
    headerFormat,
    headerText,
    templateData.headerFile,
    templateData.header_media_url
  );
  if (headerError) {
    if (headerFormat === "TEXT") {
      errors.headerText = headerError;
    } else {
      errors.headerFile = headerError;
    }
  }

  // Validate variables
  const variableErrors = validateVariables(templateData.variables);
  if (variableErrors) {
    Object.assign(errors, variableErrors);
  }

  // Validate URL buttons
  const buttonsComponent = templateData.components.find(c => c.type === "BUTTONS");
  if (buttonsComponent?.buttons) {
    const urlButtonErrors = validateUrlButtons(buttonsComponent.buttons);
    if (urlButtonErrors) {
      Object.assign(errors, urlButtonErrors);
    }
  }

  return errors;
};

